import React, { useEffect, useRef, useState } from "react";
import CaretDown from "../../../assets/svg/icons/common/CaretDown";
import DarkCross from "../../../assets/svg/icons/common/dark-cross";
import SearchIcon from "../../../assets/svg/icons/common/search";
import TextInput from "../text-input";
import formBaseStyles from "../text-input/styles.module.scss";
import styles from "./styles.module.scss";
import useDomainStore from "../../../stores/domain";
import Spinner from "../../Loaders/Spinner";
import { isScrolledNearBottom } from "../../../utils/is-scrolled-near-bottom";

const Option = ({ children, option, handleOptionSelect }) => {
  if (!handleOptionSelect) {
    return (
      <div
        className={`${styles.option} ${!handleOptionSelect && styles.optionNoSelect}`}
      >
        {children}
      </div>
    );
  }

  return (
    <div
      tabIndex={0}
      className={`${styles.option}`}
      onClick={() => handleOptionSelect(option)}
      onKeyUp={(e) => e.key === "Enter" && handleOptionSelect(option)}
    >
      {children}
    </div>
  );
};

export const TagInput = ({
  id,
  label,
  placeholder,
  value,
  options,
  onChange,
  onSearchChange,
  hideChips = false,
  touched = false,
  errored = false,
  error = "",
  search = false,
  async = false,
  loading = false,
  className = "",
  debounceDelay = 250,
  onLoadMore = null,
  hasMore = false,
  loadingMore = false,
  serverFiltered = false,
}) => {
  const { isKurvDomain } = useDomainStore();
  const selectContainerRef = useRef(null);
  const [selected, setSelected] = useState(value);
  const [searchTerm, setSearchTerm] = useState("");
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const debounceRef = useRef(null);

  const handleOptionSelect = (option) => {
    if (selected && selected?.some((s) => s.value === option.value)) {
      return;
    }

    setSelected((prev) => [...prev, option]);
    onChange([...selected, option]);
    setDropdownOpen(false);
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        selectContainerRef.current &&
        !selectContainerRef.current.contains(e.target)
      ) {
        setDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  useEffect(() => {
    if (!dropdownOpen) {
      setSearchTerm("");
      // Server-filtered options would otherwise stay narrowed to the cleared term.
      if (serverFiltered && searchTerm && onSearchChange) {
        if (debounceRef.current) clearTimeout(debounceRef.current);
        onSearchChange("");
      }
    }
  }, [dropdownOpen]);

  useEffect(() => {
    setSelected(value || []);
  }, [value]);

  return (
    <div className={`${isKurvDomain ? styles.kurv_theme : ""} ${className}`}>
      <label className={formBaseStyles.label} htmlFor={id}>
        {label}
      </label>
      <div className={`${styles.selectContainer}`} ref={selectContainerRef}>
        <TextInput
          type="text"
          placeholder={placeholder || "Search..."}
          prefix={<SearchIcon />}
          suffix={
            <div className={`${styles.caret} ${dropdownOpen && styles.open}`}>
              <CaretDown />
            </div>
          }
          value={searchTerm}
          onChange={(e) => {
            const value = e.target.value;
            setSearchTerm(value);
            if (!search || !onSearchChange) return;

            if (!async) {
              onSearchChange(value);
              return;
            }

            if (debounceRef.current) clearTimeout(debounceRef.current);
            debounceRef.current = setTimeout(
              () => onSearchChange(value),
              debounceDelay,
            );
          }}
          onClick={() => setDropdownOpen(!dropdownOpen)}
        />
        <div
          className={`${styles.dropdownContainer} ${dropdownOpen && styles.open}`}
        >
          <div
            className={`${styles.dropdown}`}
            onScroll={(e) => {
              if (!async || !onLoadMore || !hasMore || loadingMore) return;
              if (isScrolledNearBottom(e.target)) {
                onLoadMore();
              }
            }}
          >
            {(() => {
              const matchedOptions = (options || []).filter(
                (option) =>
                  serverFiltered ||
                  option.label.toLowerCase().includes(searchTerm.toLowerCase()),
              );

              if (matchedOptions.length === 0 && searchTerm.length > 0) {
                return <Option>No results found</Option>;
              }

              const visibleOptions = matchedOptions.filter(
                (option) => !selected?.some((s) => s.value === option.value),
              );

              return visibleOptions.map((option) => (
                <Option
                  key={option.value}
                  option={option}
                  handleOptionSelect={handleOptionSelect}
                >
                  {option.label}
                </Option>
              ));
            })()}
            {async && (loading || loadingMore) && (
              <div className={styles.loadingSpinner}>
                <Spinner size={18} />
              </div>
            )}
          </div>
        </div>
        {touched && errored && error && <p className={styles.error}>{error}</p>}
      </div>

      {!hideChips && selected && selected.length > 0 && (
        <div className={styles.selectedOptions}>
          {selected.map((option) => (
            <div className={styles.selectedOption} key={option.value}>
              {option.label}

              <span
                className={styles.removeSelectedOption}
                onClick={() => {
                  setSelected((prev) =>
                    prev.filter((sel) => sel.value !== option.value),
                  );
                  onChange(
                    selected.filter((sel) => sel.value !== option.value),
                  );
                }}
              >
                <DarkCross />
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default TagInput;
